import { calculateLayoutElk } from '../../layoutEngine';
import {
  clonePlainElkResult,
  makeElkLayoutInputFingerprint,
  pickMapForIds,
  shallowShellFromCachedElk,
} from './elkLayoutCache';
import {
  getConnectedLayoutTasks,
  mergeIsolatedSpawnPositions,
  preserveIsolatedManualPositions,
  connectedLayoutPinKey,
  heldPositionsForImplicitLayout,
} from './isolatedSpawnPositions';

/**
 * Run or reuse ELK for this layout pass. Returns `{ aborted: true }` when the
 * effect was cancelled or a newer layoutVersion superseded this run.
 */
export async function computeElkLayoutResult({
  isCancelled,
  layoutVersionWhenStarted,
  layoutVersionRef,
  isExplicitAnimatedLayout,
  tasksForLayoutRef,
  nodeDimensionsRef,
  customPositionsRef,
  lastCommittedElkPositionsRef,
  lastCommittedElkEdgePathsRef,
  lastCommittedLayoutPinKeyRef = null,
  lastElkInputFingerprintRef,
  lastElkPlainResultRef,
  initialElkSettlementRef,
  lockedTaskIdSignature,
  lockedDependencySignature,
  chainOrder,
  chainOrderKey,
}) {
  const layoutTasks = tasksForLayoutRef.current;
  // Implicit layout: keep loose "drop here" missions out of ELK. A second
  // disconnected component switches ELK into multi-chain packing, which
  // re-origins the existing graph a few pixels up and left.
  const elkInputTasks = isExplicitAnimatedLayout
    ? layoutTasks
    : getConnectedLayoutTasks(layoutTasks);
  const elkInputIds = new Set(elkInputTasks.map((t) => t.id));
  const nodeDimensions = nodeDimensionsRef.current;
  const layoutPinKey = connectedLayoutPinKey(
    layoutTasks,
    nodeDimensions,
    lockedDependencySignature,
  );
  const fp = makeElkLayoutInputFingerprint(
    elkInputTasks,
    pickMapForIds(nodeDimensions, elkInputIds),
    pickMapForIds(customPositionsRef?.current, elkInputIds),
    isExplicitAnimatedLayout ? lockedTaskIdSignature : elkInputTasks.map((t) => t.id).sort().join('|'),
    isExplicitAnimatedLayout ? lockedDependencySignature : '',
    chainOrderKey,
  );

  const canReuseCachedElk =
    !initialElkSettlementRef.current &&
    lastElkPlainResultRef.current &&
    fp === lastElkInputFingerprintRef.current;

  let result;
  if (elkInputTasks.length === 0) {
    result = {
      positions: mergeIsolatedSpawnPositions(
        lastElkPlainResultRef.current?.positions,
        layoutTasks,
        customPositionsRef?.current,
      ),
      edgePaths: lastElkPlainResultRef.current?.edgePaths || {},
      chainInfo: lastElkPlainResultRef.current?.chainInfo || [],
    };
  } else if (canReuseCachedElk) {
    result = shallowShellFromCachedElk(lastElkPlainResultRef.current);
  } else {
    result = await calculateLayoutElk(
      elkInputTasks,
      nodeDimensions,
      chainOrder,
    );
    if (isCancelled()) return { aborted: true };
    if (layoutVersionWhenStarted !== layoutVersionRef.current) return { aborted: true };

    if (!result || !result.positions) {
      lastElkInputFingerprintRef.current = null;
      lastElkPlainResultRef.current = null;
    } else {
      lastElkInputFingerprintRef.current = fp;
      lastElkPlainResultRef.current = clonePlainElkResult(result);
    }
  }

  if (isCancelled()) return { aborted: true };
  if (layoutVersionWhenStarted !== layoutVersionRef.current) return { aborted: true };

  if (result?.positions && !isExplicitAnimatedLayout) {
    const withSpawns = mergeIsolatedSpawnPositions(
      result.positions,
      layoutTasks,
      customPositionsRef?.current,
    );
    const held = heldPositionsForImplicitLayout(
      lastCommittedLayoutPinKeyRef?.current,
      layoutPinKey,
      lastCommittedElkPositionsRef.current,
    );
    const pinned = preserveIsolatedManualPositions(
      withSpawns,
      layoutTasks,
      customPositionsRef?.current,
      held,
    );
    if (pinned !== result.positions) {
      const restoredHeld = pinned !== withSpawns;
      result = {
        ...result,
        positions: pinned,
        ...(restoredHeld
          ? { edgePaths: lastCommittedElkEdgePathsRef?.current || result.edgePaths }
          : {}),
      };
      if (lastElkPlainResultRef.current) {
        lastElkPlainResultRef.current = clonePlainElkResult(result);
      }
    }
  }

  if (result) {
    result = { ...result, __layoutPinKey: layoutPinKey };
  }

  return { result, layoutTasks };
}
